import type { PortalCustomization } from "@redotech/customer-portal-sdk/rpc/schema/portal-customization/get-portal-customization";
import { useTriggerLoadWithInput } from "@redotech/react-util/load";
import { useStyleProperty } from "@redotech/react-util/style";
import { RpcClientContext } from "@redotech/redo-customer-portal-shared/contexts/rpc";
import {
  createContext,
  memo,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
} from "react";
import { createPortal } from "react-dom";
import { SettingsContext } from "../contexts/settings";
import { PortalCustomizationStyleTag } from "./portal-customization-style-tag";
import { resolveCustomizationImage } from "./resolve-customization-field";

export interface CustomizationContextValue {
  customization: PortalCustomization | null;
  preview: boolean;
}

export const CustomizationContext = createContext<CustomizationContextValue>({
  customization: null,
  preview: false,
});

export const CustomizationProvider = memo(function CustomizationProvider({
  children,
  preview = false,
  customizationOverride,
}: {
  children: ReactNode | ReactNode[];
  preview?: boolean;
  /** Optional in-memory customization that bypasses the RPC fetch. Used by
   * the merchant-app portal builder to show unsaved edits live. */
  customizationOverride?: PortalCustomization | null;
}) {
  const rpcClient = useContext(RpcClientContext);
  const settings = useContext(SettingsContext);

  const [customizationLoad, refresh] = useTriggerLoadWithInput(
    async (_: void) => {
      if (!rpcClient) return null;
      const response = await rpcClient.getPortalCustomization({});
      return response.customization;
    },
  );

  useEffect(() => {
    if (customizationOverride !== undefined) return;
    if (!rpcClient) return;
    refresh();
  }, [refresh, customizationOverride, rpcClient]);

  const customization: PortalCustomization | null =
    customizationOverride !== undefined
      ? (customizationOverride ?? null)
      : (customizationLoad.value ?? null);

  // Apply the favicon to `<head>` as a side effect.
  const faviconUrl = useMemo(
    () =>
      resolveCustomizationImage(
        customization?.branding?.faviconUrl,
        settings?.brandKit?.images.faviconUrl ||
          settings?.brandKit?.images.logoUrl,
      ),
    [customization, settings],
  );
  useEffect(() => {
    if (preview || !faviconUrl) return;
    const link = document.createElement("link");
    link.rel = "icon";
    link.type = "image/x-icon";
    link.href = faviconUrl;
    document.head.append(link);
    return () => {
      link.remove();
    };
  }, [faviconUrl, preview]);

  // In preview mode, force every document-level CSS var to `inherit` so the
  // merchant-app shell isn't themed by the previewed portal's settings.
  useStyleProperty(
    document.documentElement,
    "--redo-primary-color",
    preview
      ? "inherit"
      : (settings?.theme.button_background_color ?? "inherit"),
  );
  useStyleProperty(
    document.documentElement,
    "--redo-text-color",
    preview ? "inherit" : (settings?.theme.button_text_color ?? "inherit"),
  );
  useStyleProperty(
    document.documentElement,
    "--redo-primary-button-color",
    preview
      ? "inherit"
      : (settings?.theme.button_background_color ?? "inherit"),
  );
  useStyleProperty(
    document.documentElement,
    "--redo-accent-color",
    preview ? "inherit" : (settings?.theme.accent_color ?? "inherit"),
  );
  useStyleProperty(
    document.documentElement,
    "--redo-accent-color-background",
    preview
      ? "inherit"
      : (settings?.theme.accent_background_color ?? "inherit"),
  );
  useStyleProperty(
    document.documentElement,
    "--return-app-body-font-family",
    preview ? "inherit" : (settings?.theme.body_font_family ?? "inherit"),
  );
  useStyleProperty(
    document.documentElement,
    "--return-app-title-font-family",
    preview ? "inherit" : (settings?.theme.title_font_family ?? "inherit"),
  );

  const value = useMemo<CustomizationContextValue>(
    () => ({ customization, preview }),
    [customization, preview],
  );

  const newCustomCss = settings?.theme.new_custom_css;

  const hasLoadedOnce =
    customizationOverride !== undefined ||
    customizationLoad.value !== undefined ||
    !!customizationLoad.error;

  // Prevents legacy customizations from briefly showing before, likely can be removed when we fully remove old styling in favor of new portal builder
  if (!hasLoadedOnce) return null;

  return (
    <CustomizationContext.Provider value={value}>
      {!preview &&
        newCustomCss &&
        createPortal(<style>{newCustomCss}</style>, document.body)}
      {!preview && <PortalCustomizationStyleTag />}
      {children}
    </CustomizationContext.Provider>
  );
});
